import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  FaShieldHalved,
  FaGift,
  FaGamepad,
  FaCreditCard,
  FaMobileScreenButton,
  FaHeadset,
  FaCircleCheck,
  FaClock,
  FaPause,
  FaLock,
  FaPhone,
  FaCircleInfo,
  FaStar,
  FaChevronRight,
  FaPlus,
  FaMinus,
} from 'react-icons/fa6';
import { HiOutlineShieldCheck } from 'react-icons/hi2';
import { CASINOS } from '../data/casinos';
import { TRUST_LINKS } from '../data/site';
import CasinoCard from '../components/casinocard';
import RgCallout from '../components/rgcallout';
import './home.css';

const FAQ_ITEMS = [
  {
    q: 'Are all casinos on this site licensed in the UK?',
    a: 'Yes. Every casino featured on London Casino Deals holds a valid licence from the UK Gambling Commission (UKGC). We only list regulated operators.',
  },
  {
    q: 'How do you rate casino sites?',
    a: 'We assess each casino across multiple criteria including game variety, bonus value and fairness, payment options, customer support, mobile performance, and responsible gambling tools.',
  },
  {
    q: 'Are the bonus offers guaranteed?',
    a: 'Bonus offers are subject to each operator\'s terms and conditions, including wagering requirements and eligibility criteria. Always read the full T&Cs before claiming a bonus.',
  },
  {
    q: 'What is the minimum age to gamble in the UK?',
    a: 'You must be 18 years old or over to gamble legally in the United Kingdom. All sites on this portal enforce strict age verification.',
  },
  {
    q: 'How do I self-exclude if I need a break?',
    a: 'GAMSTOP provides a free national self-exclusion scheme for UK players. Visit gamstop.co.uk to register. Individual casinos also offer their own self-exclusion tools.',
  },
];

function FaqItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item${open ? ' faq-item--open' : ''}`}>
      <button className="faq-item__question" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span>{item.q}</span>
        <span className="faq-item__icon" aria-hidden="true">
          {open ? <FaMinus size={16} /> : <FaPlus size={16} />}
        </span>
      </button>
      {open && <div className="faq-item__answer">{item.a}</div>}
    </div>
  );
}

export default function Home() {
  return (
    <>
      <Helmet>
        <title>London Casino Deals — Top UK Casino Sites Compared | Best Bonuses 2026</title>
        <meta name="description" content="Compare the best UKGC-licensed casino sites in the UK. Find exclusive welcome bonuses, honest ratings, and fast withdrawal casinos at London Casino Deals." />
        <meta property="og:title" content="London Casino Deals — Compare UK Casino Sites & Bonuses" />
        <meta property="og:description" content="Honest, independent UK casino comparison. Top-rated UKGC-licensed sites, best bonuses, and safer gambling tools — all in one place." />
        <link rel="canonical" href="https://londoncasinodeals.com/" />
      </Helmet>

      {/* HERO */}
      <section className="hero">
        <div className="hero__bg" aria-hidden="true" />
        <div className="container hero__inner">
          <div className="hero__content">
            <div className="hero__eyebrow">
              <span className="badge badge--cyan badge--with-icon">
                <HiOutlineShieldCheck size={16} aria-hidden />
                UK Licensed Casinos Only
              </span>
            </div>
            <h1 className="hero__title">
              Find the <span className="hero__title-accent">Best UK Casino</span> Deals in One Place
            </h1>
            <p className="hero__subtitle">
              Independent, expert-reviewed casino comparisons. All sites are UKGC-licensed, fully verified, and rated across 12 key criteria. Compare bonuses, games, and withdrawals in seconds.
            </p>
            <div className="hero__actions">
              <a href="#casinos" className="btn btn--primary btn--lg">See Top Casinos</a>
              <Link to="/how-it-works" className="btn btn--outline-white btn--lg">How We Review</Link>
            </div>
            <div className="hero__trust-row">
              <span className="hero__trust-item">
                <FaCircleCheck className="hero__trust-ic" size={16} aria-hidden />
                UKGC Licensed Only
              </span>
              <span className="hero__trust-item">
                <FaCircleCheck className="hero__trust-ic" size={16} aria-hidden />
                No Hidden Fees
              </span>
              <span className="hero__trust-item">
                <FaCircleCheck className="hero__trust-ic" size={16} aria-hidden />
                18+ Only
              </span>
            </div>
          </div>
          <div className="hero__stats">
            <div className="hero__stat">
              <span className="hero__stat-number">3</span>
              <span className="hero__stat-label">Verified Casinos</span>
            </div>
            <div className="hero__stat">
              <span className="hero__stat-number">12</span>
              <span className="hero__stat-label">Review Criteria</span>
            </div>
            <div className="hero__stat">
              <span className="hero__stat-number">100%</span>
              <span className="hero__stat-label">UKGC Licensed</span>
            </div>
          </div>
        </div>
      </section>

      {/* DISCLOSURE STRIP */}
      <div className="disclosure-strip">
        <div className="container disclosure-strip__inner">
          <span className="disclosure-strip__icon" aria-hidden="true">
            <FaCircleInfo size={18} />
          </span>
          <div className="disclosure-strip__body">
            <p>
              <strong>Commercial disclosure:</strong> London Casino Deals earns affiliate commission when you sign up via our links. This does not influence our ratings. All listed casinos hold a UK Gambling Commission licence. Bonus terms apply — always read the full T&amp;Cs. 18+ · Gamble responsibly.
            </p>
            <p className="disclosure-strip__more">
              We are not a gambling operator. Nothing on this site is financial, legal, or medical advice. Offers change; operators set their own rules. If gambling is causing stress, debt, or harm to you or your family, stop and use the free support services linked on this page.
            </p>
          </div>
        </div>
      </div>

      <div className="age-risk-strip" role="note">
        <div className="container age-risk-strip__inner">
          <span className="age-risk-strip__badge" aria-hidden="true">18+</span>
          <p className="age-risk-strip__text">
            <strong>UK adults only.</strong> Remote gambling is illegal for anyone under 18. Operators will verify your age and identity. Do not attempt to gamble if you are excluded or if it is illegal where you are.
          </p>
        </div>
      </div>

      <div className="home-rg-slot">
        <div className="container">
          <RgCallout variant="compact" />
        </div>
      </div>

      {/* ALL 3 CASINOS */}
      <section className="section section--darker" id="casinos">
        <div className="container">
          <div className="section__header">
            <span className="section__eyebrow">Casino Comparison</span>
            <h2 className="section__title">All Reviewed UK Casino Sites</h2>
            <p className="section__subtitle">Every site is independently reviewed, UKGC-licensed, and rated across 12 key criteria</p>
          </div>
          <div className="casino-grid">
            {CASINOS.map((casino, i) => (
              <CasinoCard key={casino.id} casino={casino} rank={i + 1} />
            ))}
          </div>
          <p className="casino-grid__disclaimer">18+ · New players only · T&amp;Cs apply · Please gamble responsibly · Wagering requirements apply</p>
          <RgCallout variant="boxed" className="home-casinos-rg" />
        </div>
      </section>

      {/* BONUS HIGHLIGHT */}
      <section className="section section--white">
        <div className="container">
          <div className="section__header">
            <span className="section__eyebrow">Current Offers</span>
            <h2 className="section__title">Top Welcome Bonuses Available Today</h2>
            <p className="section__subtitle">Compare the best first-deposit bonus offers from our reviewed UK casino sites</p>
          </div>
          <RgCallout variant="compact" className="home-bonus-rg" />
          <div className="bonus-table">
            {CASINOS.map((casino, i) => (
              <div key={casino.id} className="bonus-row">
                <div className="bonus-row__rank">{i + 1}</div>
                <div className="bonus-row__logo">
                  <img src={casino.logo} alt={casino.name} />
                </div>
                <div className="bonus-row__name">{casino.name}</div>
                <div className="bonus-row__offer">
                  <strong>{casino.bonusHeadline}</strong>
                  <span>{casino.bonusDetails}</span>
                </div>
                <div className="bonus-row__rating">
                  <span className="bonus-row__stars" aria-hidden>
                    {Array(Math.floor(casino.rating)).fill(null).map((_, si) => (
                      <FaStar key={si} className="bonus-row__star" size={14} />
                    ))}
                  </span>
                  <span className="bonus-row__rating-label">{casino.ratingLabel}</span>
                </div>
                <a
                  href={casino.affiliateLink}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  className="btn btn--primary btn--sm"
                >
                  Claim Now
                </a>
              </div>
            ))}
          </div>
          <p className="section__disclaimer">18+ · T&amp;Cs apply · Wagering requirements apply · Responsible gambling</p>
          <div className="section__cta-wrap">
            <Link to="/welcome-bonuses" className="btn btn--secondary">Compare All Bonuses in Detail</Link>
          </div>
        </div>
      </section>

      {/* HOW WE REVIEW */}
      <section className="section section--darker">
        <div className="container">
          <div className="section__header">
            <span className="section__eyebrow">Our Process</span>
            <h2 className="section__title">How We Review UK Casinos</h2>
            <p className="section__subtitle">Every review follows a structured process across 12 independent criteria</p>
          </div>
          <div className="criteria-grid">
            {[
              { Icon: FaShieldHalved, title: 'Licence Check', desc: 'We verify each casino holds a valid, active UKGC licence before anything else.' },
              { Icon: FaGift, title: 'Bonus Fairness', desc: 'We analyse wagering requirements, eligible games, and payout caps against industry standards.' },
              { Icon: FaGamepad, title: 'Game Library', desc: 'We assess the breadth of slots, table games, and live casino content from top software providers.' },
              { Icon: FaCreditCard, title: 'Payments', desc: 'We check available deposit and withdrawal methods, processing times, and minimum limits.' },
              { Icon: FaMobileScreenButton, title: 'Mobile Experience', desc: 'All casinos are tested on mobile browsers and, where available, dedicated apps.' },
              { Icon: FaHeadset, title: 'Customer Support', desc: 'We evaluate support availability, response speed, and quality across live chat, email, and phone.' },
            ].map((c) => (
              <div key={c.title} className="criteria-card">
                <div className="criteria-card__icon" aria-hidden="true">
                  <c.Icon size={26} />
                </div>
                <h3 className="criteria-card__title">{c.title}</h3>
                <p className="criteria-card__desc">{c.desc}</p>
              </div>
            ))}
          </div>
          <div className="section__cta-wrap">
            <Link to="/how-it-works" className="btn btn--primary">Read Our Full Methodology</Link>
          </div>
        </div>
      </section>

      {/* WHY LICENSED CASINOS */}
      <section className="section section--white">
        <div className="container">
          <div className="why-licensed">
            <div className="why-licensed__content">
              <span className="section__eyebrow">Player Safety</span>
              <h2 className="section__title" style={{ textAlign: 'left', marginBottom: 16 }}>Why Only Play at UKGC-Licensed Casinos?</h2>
              <p className="why-licensed__intro">The UK Gambling Commission sets some of the strictest standards in the world. Only licensed operators can legally offer gambling services to UK residents.</p>
              <ul className="why-licensed__list">
                {[
                  'Player funds are held in separate accounts, protected from business failure',
                  'All games are independently tested for fairness and correct RTP',
                  'Strict age verification prevents underage gambling',
                  'Self-exclusion tools must be offered, including GAMSTOP integration',
                  'Marketing must be transparent and not mislead players',
                  'Dispute resolution via the ADR (Alternative Dispute Resolution) scheme',
                ].map((item) => (
                  <li key={item} className="why-licensed__item">
                    <span className="why-licensed__check" aria-hidden="true">
                      <FaCircleCheck size={18} />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href="https://www.gamblingcommission.gov.uk/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="btn btn--secondary"
              >
                Visit UKGC Website
              </a>
            </div>
            <div className="why-licensed__trust">
              {TRUST_LINKS.map((t) => (
                <a
                  key={t.name}
                  href={t.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="why-licensed__trust-card"
                >
                  <span className="why-licensed__trust-logo-wrap">
                    <img src={t.logo} alt="" className="why-licensed__trust-logo" />
                  </span>
                  <div>
                    <div className="why-licensed__trust-name">{t.name}</div>
                    <div className="why-licensed__trust-desc">{t.description}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* RESPONSIBLE GAMBLING */}
      <section className="section rg-section">
        <div className="container">
          <div className="rg-section__inner">
            <div className="rg-section__content">
              <span className="section__eyebrow" style={{ color: '#22d3ee' }}>Safer Gambling</span>
              <h2 className="section__title" style={{ textAlign: 'left', color: '#ffffff', marginBottom: 12 }}>
                Gambling Should Be Fun — Keep It That Way
              </h2>
              <p className="rg-section__text">
                We are committed to promoting responsible gambling. If gambling is no longer enjoyable or is causing harm, free support is available 24/7.
              </p>
              <div className="rg-section__tools">
                <div className="rg-section__tool">
                  <span className="rg-section__tool-ic" aria-hidden><FaClock size={18} /></span>
                  Set deposit limits before you play
                </div>
                <div className="rg-section__tool">
                  <span className="rg-section__tool-ic" aria-hidden><FaPause size={18} /></span>
                  Use take-a-break and cool-off features
                </div>
                <div className="rg-section__tool">
                  <span className="rg-section__tool-ic" aria-hidden><FaLock size={18} /></span>
                  Self-exclude via GAMSTOP if needed
                </div>
                <div className="rg-section__tool">
                  <span className="rg-section__tool-ic" aria-hidden><FaPhone size={18} /></span>
                  Call the GamCare helpline: 0808 8020 133
                </div>
              </div>
            </div>
            <div className="rg-section__links">
              {TRUST_LINKS.slice(1).map((t) => (
                <a
                  key={t.name}
                  href={t.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="rg-section__link"
                >
                  <span className="rg-section__link-name">{t.name}</span>
                  <FaChevronRight className="rg-section__link-arrow" size={14} aria-hidden />
                </a>
              ))}
              <Link to="/responsible-gambling" className="rg-section__link rg-section__link--internal">
                <span className="rg-section__link-name">Our Responsible Gambling Guide</span>
                <FaChevronRight className="rg-section__link-arrow" size={14} aria-hidden />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section section--white">
        <div className="container">
          <RgCallout variant="compact" className="home-faq-rg" />
          <div className="section__header">
            <span className="section__eyebrow">FAQ</span>
            <h2 className="section__title">Frequently Asked Questions</h2>
          </div>
          <div className="faq-list">
            {FAQ_ITEMS.map((item) => (
              <FaqItem key={item.q} item={item} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
